import _ from 'lodash';
import moment from 'moment';

import AgentActivity, { CommunicationSessionStatus, TaskStatus } from 'models/agent_activity';
import Communicator from 'models/communicator';
import ConversationItemType from 'models/conversation_item_type';
import { getActiveConversationId } from 'actions/conversation/lib/conversation_helpers';
import InteractionType from 'models/interaction_type';
import ItemLinkContent from 'models/item_link_content';
import PhoneCall from 'models/phone_call';

export default class SetActivity {
  constructor(context) {
    this.context = context;
  }

  run({ agentId, availabilityChannels, channel, conversationId, reasonId, routingGroupId, timestamp, type }) {
    agentId = agentId || (this.currentAgent && this.currentAgent.id);
    let customerId = this.currentCustomerId;
    conversationId = conversationId || this.getCurrentConversationId(customerId);
    let communicationSession = this.getCommunicationSession(customerId);

    let agentActivity = {
      agentId,
      availabilityChannels,
      channel,
      conversationId,
      customerId,
      reasonId,
      routingGroupId,
      timestamp,
      type,
    };

    if (!_.isEmpty(communicationSession)) {
      agentActivity.communicationSessionId = communicationSession.id;
      agentActivity.communicationSessionStatus = communicationSession.status;
    }

    let task = this.getTaskFromPanel(customerId);
    if (task) {
      agentActivity.taskId = task.id;
      agentActivity.taskStatus = task.status;
    }

    let activity = AgentActivity.create(agentActivity);

    this.context.stores.agentActivity.set(activity);
  }

  get currentAgent() {
    return this.context.stores.currentAgent.get();
  }

  get currentCustomerId() {
    return this.context.stores.currentLocation.get().customerId;
  }

  getCurrentConversationId(customerId) {
    if (customerId) {
      const { conversations } = this.context.stores.customers.storesFor(customerId);
      return getActiveConversationId(conversations);
    }
  }

  getCommunicationSession(customerId) {
    let activeCallSession = this.context.stores.activeCall.get();

    let activeCallSessionId =
      activeCallSession && activeCallSession.conversationItem ? activeCallSession.conversationItem.id : null;

    if (activeCallSessionId) {
      return { id: activeCallSessionId, status: CommunicationSessionStatus.ACTIVE };
    }

    let activeSession = this.context.stores.activeSessions.findBy({
      customer: { id: customerId },
    });

    if (activeSession) {
      return {
        id: getCommunicationSessionIdFromActiveSession(activeSession),
        status: CommunicationSessionStatus.ACTIVE,
      };
    }

    if (customerId && this.context.stores.customers.has({ id: customerId })) {
      const { conversations, conversationHistory } = this.context.stores.customers.storesFor(customerId);

      let sessionIdFromHistory = getLastSessionIdFromHistory(conversations, conversationHistory);

      if (sessionIdFromHistory) {
        return {
          id: sessionIdFromHistory,
          status: CommunicationSessionStatus.ENDED,
        };
      }
    }
    return null;
  }

  getTaskFromPanel(customerId) {
    const commentPanel = this.context.stores.commentPanel.get();

    if (!commentPanel.lastOpenedAt) {
      return;
    }

    const itemId = commentPanel.selectedItemId;
    if (!itemId) {
      return;
    }

    if (customerId && this.context.stores.customers.has({ id: customerId })) {
      const { conversationHistory } = this.context.stores.customers.storesFor(customerId);
      let item = conversationHistory.findBy({ id: itemId });

      if (item && conversationHistory.isPending(itemId)) {
        item = conversationHistory.getPending(itemId);
      }

      if (!item) {
        return;
      }

      let taskId = itemId;
      let taskIsClosed = !!item.content.closedAt;
      if (item.content instanceof ItemLinkContent) {
        taskId = item.content.itemId;
        taskIsClosed = !!item.content.originalContent.closedAt;
      }

      return {
        id: taskId,
        status: taskIsClosed ? TaskStatus.CLOSED : TaskStatus.OPEN,
      };
    }

    // return null otherwise
  }
}

const interactionTypeRank = Object.freeze([
  InteractionType.TASK,
  InteractionType.ABANDONED_CALL,
  InteractionType.VOICEMAIL,
  InteractionType.EMAIL,
  InteractionType.WHATSAPP,
  InteractionType.TWITTER,
  InteractionType.FB_MESSENGER,
  InteractionType.SMS,
  InteractionType.CHAT,
  InteractionType.PHONE_CALL,
]);

function getInteractionTypeRank(interactionType) {
  return interactionTypeRank.indexOf(interactionType);
}

function getCommunicationSessionIdFromActiveSession(activeSession) {
  return _.orderBy(activeSession.queueItem.sessions, [session => getInteractionTypeRank(session.type)], ['desc'])[0].id;
}

function getLastSessionIdFromHistory(conversations, conversationHistory) {
  const currentConversationId = getActiveConversationId(conversations);

  if (!currentConversationId) {
    return null;
  }

  let lastCustomerMessage = conversationHistory.findBy({
    filter: item =>
      item.conversationId === currentConversationId &&
      item.initiator.type === Communicator.CUSTOMER &&
      !isCommunicationSessionEndedItem(item),
    sortBy: item => -1 * moment(item.timestamp).valueOf(),
  });

  let lastSessionEndedItem = conversationHistory.findBy({
    filter: item => isCommunicationSessionEndedItem(item),
    sortBy: item => -1 * moment(item.timestamp).valueOf(),
  });

  if (!lastSessionEndedItem) {
    return null;
  }

  if (
    !lastCustomerMessage ||
    moment(_.get(lastSessionEndedItem, 'timestamp')).isAfter(_.get(lastCustomerMessage, 'timestamp'))
  ) {
    return lastSessionEndedItem.content instanceof PhoneCall
      ? lastSessionEndedItem.id
      : lastSessionEndedItem.content.sessionId;
  }
  return null;
}

function isCommunicationSessionEndedItem(item) {
  return (
    (item.content.type === ConversationItemType.PHONE_CALL && item.content.status === PhoneCall.Status.COMPLETED) ||
    item.content.type === ConversationItemType.SESSION_ENDED
  );
}



// WEBPACK FOOTER //
// ./src/scripts/application/actions/agent_activity/set_activity.js